import React from 'react';
import { CheckIcon, LanguagesIcon, PencilLineIcon, SaveIcon, SparkleIcon } from 'lucide-react';
import { profileBlocks, jobDescription, type ArtifactTab } from '../../lib/script';
import { criteria } from '../../lib/data';
type ArtifactPaneProps = {
  tab: ArtifactTab;
  onTabChange: (tab: ArtifactTab) => void;
  availableTabs: ArtifactTab[];
  profileCount: number;
  showDescription: boolean;
  criteriaCount: number;
};
const TAB_LABELS: Record<ArtifactTab, string> = {
  profil: 'Profil de réussite',
  description: 'Description',
  criteres: 'Critères'
};
function ToolbarButton({
  label,
  icon: Icon
}: {
  label: string;
  icon: typeof PencilLineIcon;
}) {
  return <button type="button" className="flex items-center gap-1.5 rounded-card border border-line bg-white px-2.5 py-1.5 text-[12px] text-ink transition-colors hover:border-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2" data-id="element-60" data-mp="1ck9cbuwtqrg4">
      <Icon className="h-[14px] w-[14px] text-muted" strokeWidth={1.75} data-id="element-61" data-mp="17f6al3dkv3z" />
      {label}
    </button>;
}
function EmptyState() {
  return <div className="flex h-full flex-col items-center justify-center px-8 text-center" data-id="element-62" data-mp="288n18jxsbyxq">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-canvas" data-id="element-63" data-mp="1td72i1m9fev0">
        <SparkleIcon className="h-[18px] w-[18px] text-muted" strokeWidth={1.75} data-id="element-64" data-mp="1yaz4xpkj6jdg" />
      </div>
      <p className="mt-4 text-[13px] leading-[1.4] text-muted" data-id="element-65" data-mp="1kkdhroo1nn9r">
        Les documents produits apparaîtront ici,
        <br data-id="element-66" data-mp="1pb2b79jaaubj" />
        au fil de la conversation.
      </p>
    </div>;
}
function ProfilePanel({
  count
}: {
  count: number;
}) {
  const visible = profileBlocks.slice(0, count);
  return <div data-id="element-67" data-mp="1fkhfbtuzw3wb">
      <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-68" data-mp="1fk7q48hry0ro">
        <p className="text-[10px] font-600 tracking-[0.09em] text-muted" data-id="element-69" data-mp="11lhrgofb3t17">
          {count} / {profileBlocks.length} BLOCS
        </p>
        <div className="flex flex-wrap gap-1.5" data-id="element-70" data-mp="15lrx1zz6shfj">
          <ToolbarButton label="Modifier" icon={PencilLineIcon} data-id="element-71" data-mp="15onu7m37n22x" />
          <ToolbarButton label="Sauvegarder dans la mémoire" icon={SaveIcon} data-id="element-72" data-mp="38b2wvxmpxmf7" />
        </div>
      </div>

      <div className="mt-4 divide-y divide-line border-t border-line" data-id="element-73" data-mp="33h7191txgbn7">
        {visible.map(block => <div key={block.title} className="animate-[fadeIn_0.4s_ease-out] py-4" data-id="element-74" data-mp="2yjymzcbwgrr8">
            <p className="flex items-center gap-2 text-[10px] font-600 tracking-[0.09em] text-muted" data-id="element-75" data-mp="2tm6scajverjc">
              {block.title.toUpperCase()}
              {block.fromAnswer && <span className="rounded-full bg-brand-soft px-1.5 py-0.5 text-[9px] font-600 tracking-[0.06em] text-brand" data-id="element-76" data-mp="3mab1x75kborb">
                  DE VOTRE RÉPONSE
                </span>}
            </p>
            <ul className="mt-2 space-y-1" data-id="element-77" data-mp="3d81rehaanvdx">
              {block.items.map(item => <li key={item} className="text-[14px] leading-[1.5] text-ink" data-id="element-78" data-mp="25vsz4gpjumn6">
                  {item}
                </li>)}
            </ul>
          </div>)}
      </div>

      {count < profileBlocks.length && <p className="animate-pulse py-4 text-[12px] text-muted" data-id="element-79" data-mp="h9kg2b6wnhrd">Talenxia construit…</p>}
    </div>;
}
function DescriptionPanel() {
  return <div data-id="element-80" data-mp="qb0r8l5jnnwn">
      <div className="flex flex-wrap gap-1.5" data-id="element-81" data-mp="qba7vi4p8ipo">
        <ToolbarButton label="Ton" icon={PencilLineIcon} data-id="element-82" data-mp="v2s9m8ejqnbp" />
        <ToolbarButton label="Version courte" icon={PencilLineIcon} data-id="element-83" data-mp="v4d1fq9h7s5v" />
        <ToolbarButton label="Traduire" icon={LanguagesIcon} data-id="element-84" data-mp="102oavzvv00nk" />
        <ToolbarButton label="Sauvegarder" icon={SaveIcon} data-id="element-85" data-mp="1047wm60ab3wq" />
      </div>

      <div className="mt-5 space-y-5" data-id="element-86" data-mp="14z51d0qj6r3t">
        {jobDescription.map(section => <div key={section.heading} className="animate-[fadeIn_0.4s_ease-out]" data-id="element-87" data-mp="193utcei2jljg">
            <h4 className="font-serif text-[19px] font-500 leading-tight tracking-[-0.02em] text-ink" data-id="element-88" data-mp="1e0laz69iup6z">
              {section.heading}
            </h4>
            <p className="mt-1 text-[10px] font-600 tracking-[0.09em] text-muted" data-id="element-89" data-mp="3awar3h0x65y2">
              ← {section.source.toUpperCase()}
            </p>
            <p className="mt-2 text-[14px] leading-[1.55] text-ink" data-id="element-90" data-mp="2kht8xgf4tssh">{section.body}</p>
          </div>)}
      </div>
    </div>;
}
function typeLabel(types: string[]) {
  return types.map(t => t === 'filtrage' ? 'Filtrage' : t === 'scoring' ? 'Scoring' : t === 'entrevue' ? 'Entrevue' : 'Réussite').join(' · ');
}
function CriteriaPanel({
  count
}: {
  count: number;
}) {
  const visible = criteria.slice(0, count);
  return <div data-id="element-91" data-mp="21q0hvrjmihne">
      <p className="text-[13px] leading-[1.4] text-muted" data-id="element-92" data-mp="1wtidiss7glbl">
        Les mêmes critères servent au sourcing, au scoring, à l'entrevue et à la mesure du résultat.
      </p>

      <div className="mt-4 divide-y divide-line border-t border-line" data-id="element-93" data-mp="2bk94jylz3gdn">
        {visible.map(c => <div key={c.id} className="animate-[fadeIn_0.4s_ease-out] py-3.5" data-id="element-94" data-mp="26pd1m3f778lr">
            <div className="flex items-start justify-between gap-3" data-id="element-95" data-mp="1j0amb281druf">
              <p className="text-[14px] leading-[1.4] text-ink" data-id="element-96" data-mp="1e3k5gcoeqs7y">{c.label}</p>
              <span className="flex-none text-[13px] font-500 text-ink" data-id="element-97" data-mp="3pqdvru8cr3qw">
                {c.weight === null ? 'Élim.' : `${c.weight} %`}
              </span>
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-2" data-id="element-98" data-mp="3uinjr4pwptqj">
              <span className="text-[10px] font-600 tracking-[0.09em] text-muted" data-id="element-99" data-mp="3ftj1xon0xriy">
                {typeLabel(c.types).toUpperCase()}
              </span>
              {c.essential && <span className="rounded-full bg-brand-soft px-2 py-0.5 text-[10px] font-600 text-brand" data-id="element-100" data-mp="cciwywof5wzf">
                  Essentiel
                </span>}
            </div>
            <p className="mt-1.5 text-[12.5px] leading-[1.45] text-muted" data-id="element-101" data-mp="39htl3qxujzg">{c.rationale}</p>
          </div>)}
      </div>
    </div>;
}
export function ArtifactPane({
  tab,
  onTabChange,
  availableTabs,
  profileCount,
  showDescription,
  criteriaCount
}: ArtifactPaneProps) {
  const hasContent = availableTabs.length > 0;
  return <aside className="flex h-full min-h-0 flex-col border-l border-line bg-white" data-id="element-102" data-mp="2s00ue4lixaie">
      <div className="flex flex-none items-center gap-1 border-b border-line px-4 py-2.5" data-id="element-103" data-mp="yiv32udfc0ar">
        {hasContent ? availableTabs.map(t => <button key={t} type="button" onClick={() => onTabChange(t)} className={`rounded-card px-2.5 py-1.5 text-[12.5px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 ${tab === t ? 'bg-brand-soft font-500 text-brand' : 'text-muted hover:bg-canvas hover:text-ink'}`} data-id="element-104" data-mp="ot1snwp0pr83">
              {TAB_LABELS[t]}
            </button>) : <span className="px-1 text-[10px] font-600 tracking-[0.09em] text-muted" data-id="element-105" data-mp="x63b5huluhx">DOCUMENTS PRODUITS</span>}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5" data-id="element-106" data-mp="206w420cw4ahh">
        {!hasContent && <EmptyState data-id="element-107" data-mp="1vaoyi8oep102" />}
        {hasContent && tab === 'profil' && <ProfilePanel count={profileCount} data-id="element-108" data-mp="23rpm5dia8enb" />}
        {hasContent && tab === 'description' && showDescription && <DescriptionPanel data-id="element-109" data-mp="27rpymsltmfrq" />}
        {hasContent && tab === 'criteres' && <CriteriaPanel count={criteriaCount} data-id="element-110" data-mp="2cj80divo4kdr" />}
      </div>

      {hasContent && <div className="flex flex-none items-center gap-1.5 border-t border-line px-4 py-2.5" data-id="element-111" data-mp="2clay6p32bht1">
          <CheckIcon className="h-3.5 w-3.5 text-muted" strokeWidth={2} data-id="element-112" data-mp="2hkf6ldi5xbib" />
          <span className="text-[11px] text-muted" data-id="element-113" data-mp="1ktmrai31znhl">Version 1 · modifiable · rien n'est publié sans votre accord</span>
        </div>}
    </aside>;
}